<template>
  <div>
    <navbar :links="links" />
    <!-- Tab-like Buttons to navigate -->
    <tags-navigation-buttons />
    <!-- Header Text -->
    <div class="grey--text text--darken-2 subheading my-3">
      {{ $t('tagsManagementHint') }}
    </div>
    <v-layout
      align-center
      class="mb-3"
      wrap
    >
      <v-flex
        xs12
        md4
      />
      <v-flex
        xs12
        md4
      >
        <!-- Search -->
        <v-layout v-if="tags.length > 1">
          <v-text-field
            v-model="searchText"
            :loading="loading"
            clearable
            class="ml-0"
            prepend-icon="search"
            :placeholder="searchTextPlaceholder"
            :title="$t('search')"
            :hint="searchTextHint"
            persistent-hint
            @keyup.native.esc="searchText = ''"
          />
        </v-layout>
      </v-flex>
      <v-flex
        xs12
        md4
      >
        <v-layout justify-end>
          <!-- Add new Tag Button-->
          <v-btn
            round
            color="info"
            dark
            @click="showCreateTagDialog = true"
          >
            <v-icon left>
              add
            </v-icon>
            {{ $t('addNewTag') }}
          </v-btn>
        </v-layout>
      </v-flex>
    </v-layout>
    <!-- 'No Tags' hint -->
    <div
      v-if="orderedTags.length === 0"
      class="ml-3 mt-2 mb-4 grey--text subheading"
    >
      {{ $t('noTagsAvailable') }}
    </div>
    <!-- Tags -->
    <transition-group
      name="tags-list-transition"
      tag="div"
      class="mt-4"
    >
      <template v-for="(tag, tagIndex) in orderedTags">
        <v-card
          :key="tag.id"
          flat
          class="tag-card"
          :style="{
            opacity:
              (tagBeingEdited && !isBeingEdited(tag)) ||
              (tagBeingDeleted && !isBeingDeleted(tag))
                ? 0.33
                : 1.0,
          }"
        >
          <!-- Normal mode -->
          <v-card-text
            v-if="isBeingNormal(tag)"
            class="py-0"
          >
            <v-layout
              align-center
              wrap
            >
              <!-- Name -->
              <div
                style="overflow: hidden"
                :style="{
                  'min-width': $vuetify.breakpoint.smAndUp ? '240px' : '100%',
                }"
              >
                <v-chip
                  :title="tag.name"
                  class="ml-0 clickable"
                  :color="getTagColor(tag)"
                  @click="startEditing(tag)"
                >
                  <span :style="{color: getTagTextColor(tag)}">{{
                    tag.name
                  }}</span>
                </v-chip>
              </div>
              <!-- Buttons -->
              <div
                v-visible="canManageTag(tag)"
                class="d-flex mr-4"
                :class="{'my-2': $vuetify.breakpoint.xs}"
              >
                <!-- Remove Button -->
                <v-btn
                  color="grey lighten-2"
                  class="hoverable-icon--error"
                  icon
                  flat
                  :title="$t('remove')"
                  :disabled="!!tagBeingEdited || !!tagBeingDeleted"
                  @click="startDeleting(tag)"
                >
                  <v-icon> delete </v-icon>
                </v-btn>
                <!-- Edit Button -->
                <v-btn
                  color="grey lighten-2"
                  class="hoverable-icon--info"
                  icon
                  flat
                  :title="$t('edit')"
                  :disabled="!!tagBeingEdited || !!tagBeingDeleted"
                  @click="startEditing(tag)"
                >
                  <v-icon> edit </v-icon>
                </v-btn>
              </div>
              <v-layout align-center>
                <!-- Content/Decoder Counts -->
                <div
                  v-visible="
                    tag.num_contents &&
                      tag.num_contents.radio + tag.num_contents.tv > 0
                  "
                  class="pseudo-anchor info--text subheading nowrap"
                  :class="{'mr-3': $vuetify.breakpoint.smAndUp}"
                  style="
                    text-decoration-color: #2196f3;
                    min-width: 96px;
                    text-align: end;
                  "
                  :title="$t('numContentsWithTagClickToFilter')"
                  @click="goToContentsListFilteredByTag(tag)"
                >
                  {{ tag.num_contents.radio + tag.num_contents.tv }}
                  {{
                    $tc(
                      'contentsWithTag',
                      tag.num_contents.radio + tag.num_contents.tv
                    )
                  }}
                </div>
                <div
                  v-visible="
                    tag.num_decoders &&
                      tag.num_decoders.radio + tag.num_decoders.tv > 0
                  "
                  class="pseudo-anchor info--text subheading nowrap"
                  :class="{'mr-3': $vuetify.breakpoint.smAndUp}"
                  style="
                    text-decoration-color: #2196f3;
                    min-width: 96px;
                    text-align: end;
                  "
                  :title="$t('numDecodersWithTagClickToFilter')"
                  @click="goToPlayersListFilteredByTag(tag)"
                >
                  {{ tag.num_decoders.radio + tag.num_decoders.tv }}
                  {{ $t('decodersWithTag') }}
                </div>
                <!-- Upload Group (displayed only when there's more than one) -->
                <div
                  v-if="hasMoreThanOneUploadGroup"
                  :title="$t('group') + ': ' + getGroupNameForId(tag.group)"
                  class="caption grey--text clickable"
                  :class="{
                    'my-1': $vuetify.breakpoint.xs,
                    'ml-3': $vuetify.breakpoint.xs,
                    'ml-5': $vuetify.breakpoint.smAndUp,
                  }"
                  @click="canManageTag(tag) ? startEditing(tag) : null"
                >
                  {{ getGroupNameForId(tag.group) }}
                </div>
                <v-spacer />
              </v-layout>
            </v-layout>
          </v-card-text>
          <!-- Edit mode -->
          <v-card-text
            v-if="isBeingEdited(tag)"
            :class="[$vuetify.breakpoint.xs ? 'py-1' : 'py-2']"
          >
            <v-layout
              :align-center="$vuetify.breakpoint.smAndUp"
              :column="$vuetify.breakpoint.xs"
            >
              <v-layout
                align-baseline
                wrap
              >
                <!-- Color -->
                <v-menu
                  :close-on-content-click="false"
                  :max-width="
                    $vuetify.breakpoint.xs
                      ? $vuetify.breakpoint.width - 48
                      : 480
                  "
                  offset-y
                  lazy
                >
                  <v-btn
                    slot="activator"
                    align-center
                    flat
                    class="my-0 ml-0 mr-3 bordered--subtle"
                    style="min-width: 42px; width: 42px; height: 42px"
                    :style="getEditModeColorButtonStyle(tag)"
                    :title="$t('tagColorPickerLabel')"
                  />
                  <v-card>
                    <v-card-text>
                      <color-picker
                        :color="
                          tagBeingEdited.color || getTagColor(tagBeingEdited)
                        "
                        ignore-alpha
                        @changed="(color) => (tagBeingEdited.color = color)"
                      />
                    </v-card-text>
                  </v-card>
                </v-menu>
                <!-- Name -->
                <v-text-field
                  ref="tagNameInput"
                  v-model="tagBeingEdited.name"
                  :data-tagid="tag.id"
                  class="pr-3"
                  :class="{'grow-1': $vuetify.breakpoint.smAndUp}"
                  :label="$t('tag')"
                  required
                  :hint="$t('tagAssignmentsRemainHint')"
                  persistent-hint
                  :rules="[
                    rules.tagNameMinLength,
                    rules.tagNameMaxLength,
                    rules.editedTagUniqueName,
                  ]"
                  @keyup.native.esc="cancelEditing()"
                  @keyup.native.enter="saveEditedTag()"
                />
                <!-- Group -->
                <v-select
                  v-if="hasMoreThanOneUploadGroup"
                  v-model="tagBeingEdited.group"
                  :disabled="isTagUsed(tagBeingEdited)"
                  item-text="name"
                  item-value="id"
                  persistent-hint
                  class="mb-1 mr-3"
                  :class="{'grow-1': $vuetify.breakpoint.smAndUp}"
                  :items="uploadgroups"
                  :label="$t('chooseGroupForTag')"
                  :hint="
                    isTagUsed(tag) ? $t('chooseGroupForTagDisabledHint') : ''
                  "
                />
              </v-layout>
              <v-layout
                align-center
                :class="{'my-2': $vuetify.breakpoint.xs}"
              >
                <v-btn
                  :class="{'mr-0': $vuetify.breakpoint.xs}"
                  icon
                  flat
                  color="msr"
                  :title="$t('cancel')"
                  :disabled="busy"
                  @click="cancelEditing()"
                >
                  <v-icon>cancel</v-icon>
                </v-btn>
                <v-btn
                  :class="{'mr-0': $vuetify.breakpoint.xs}"
                  icon
                  flat
                  color="msr"
                  :title="$t('confirm')"
                  :disabled="!editedTagNameIsValid"
                  :loading="busy"
                  @click="saveEditedTag()"
                >
                  <v-icon>check</v-icon>
                </v-btn>
              </v-layout>
            </v-layout>
          </v-card-text>
          <!-- Delete mode -->
          <v-card-text
            v-if="isBeingDeleted(tag)"
            :class="[$vuetify.breakpoint.xs ? 'py-2' : 'py-3']"
          >
            <v-layout
              :align-center="$vuetify.breakpoint.smAndUp"
              :column="$vuetify.breakpoint.xs"
            >
              <span class="mr-2">{{
                $t('tagConfirmDelete', {tagName: tag.name})
              }}</span>
              <v-layout
                align-center
                :class="{'my-2': $vuetify.breakpoint.xs}"
              >
                <v-btn
                  :class="{'mr-0': $vuetify.breakpoint.xs}"
                  icon
                  flat
                  color="msr"
                  :title="$t('cancel')"
                  :disabled="busy"
                  @click="cancelDeleting()"
                >
                  <v-icon> cancel </v-icon>
                </v-btn>
                <v-btn
                  :class="{'mr-0': $vuetify.breakpoint.xs}"
                  icon
                  flat
                  color="msr"
                  :title="$t('remove')"
                  :loading="busy"
                  @click="removeTag(tag)"
                >
                  <v-icon> check </v-icon>
                </v-btn>
              </v-layout>
            </v-layout>
          </v-card-text>
        </v-card>
        <!-- Divider on mobile -->
        <v-divider
          v-if="$vuetify.breakpoint.xs && tagIndex < orderedTags.length - 1"
          :key="'divider' + tagIndex"
          class="my-3"
        />
      </template>
    </transition-group>
    <!-- Create Tag Dialog -->
    <v-dialog
      v-model="showCreateTagDialog"
      :width="480"
      lazy
      persistent
    >
      <v-card>
        <v-card-title class="pb-0">
          <v-layout align-center>
            <span class="title">{{ $t('addNewTag') }}</span>
            <v-btn
              class="ml-auto mr-0 my-0"
              flat
              icon
              :title="$t('close')"
              @click="showCreateTagDialog = false"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </v-layout>
        </v-card-title>
        <v-card-text>
          <v-layout align-center>
            <!-- New Tag Color -->
            <v-menu
              v-model="showCreateTagColorPickerMenu"
              :close-on-content-click="false"
              :max-width="
                $vuetify.breakpoint.xs ? $vuetify.breakpoint.width - 48 : 480
              "
              offset-y
              lazy
            >
              <v-btn
                slot="activator"
                align-center
                flat
                class="my-0 ml-0 mr-3 bordered--subtle"
                style="min-width: 42px; width: 42px; height: 42px"
                :style="{background: newTagColor}"
                :title="$t('tagColorPickerLabel')"
              />
              <v-card>
                <v-card-text>
                  <color-picker
                    class="mr-3"
                    :color="newTagColor"
                    ignore-alpha
                    @close="showCreateTagColorPickerMenu = false"
                    @changed="(color) => (newTagColor = color)"
                  />
                </v-card-text>
              </v-card>
            </v-menu>
            <!-- New Tag Name -->
            <v-text-field
              ref="newTagNameInput"
              v-model="newTagName"
              clearable
              :counter="tagNameMaxLength"
              :label="$t('name')"
              required
              persistent-hint
              :class="{
                'grow-1': $vuetify.breakpoint.smAndUp,
                'mr-3': $vuetify.breakpoint.smAndUp,
              }"
              :rules="[rules.tagNameMaxLength, rules.newTagUniqueName]"
              @keyup.native.esc="
                newTagName = '';
                showCreateTagDialog = false;
              "
              @keyup.native.enter="
                canSubmitCreateTagForm ? confirmCreateNewTagDialog() : null
              "
            />
          </v-layout>
          <!-- New Tag Group -->
          <v-slide-x-reverse-transition>
            <v-select
              v-if="hasMoreThanOneUploadGroup"
              v-model="newTagGroupId_"
              item-text="name"
              item-value="id"
              persistent-hint
              class="mt-3"
              :class="{
                'grow-1': $vuetify.breakpoint.smAndUp,
                'mr-3': $vuetify.breakpoint.smAndUp,
              }"
              :items="orderedUploadgroups"
              :label="$t('group')"
              :hint="$t('chooseGroupForTag')"
              style="max-width: 480px"
            />
          </v-slide-x-reverse-transition>
        </v-card-text>
        <v-card-actions class="py-3">
          <v-layout justify-center>
            <v-btn
              color="msr"
              round
              :dark="canSubmitCreateTagForm"
              :disabled="!canSubmitCreateTagForm"
              class="ml-0 mr-0"
              @click="confirmCreateNewTagDialog()"
            >
              {{ $t('confirm') }}
            </v-btn>
          </v-layout>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import orderBy from 'lodash.orderby';
import tinycolor from 'tinycolor2/tinycolor';

import {deepCopyObject} from '@/utils';
import matchesFilter from '@/matchesFilter';

import tagsMixin from '@/mixins/tags';

import ColorPicker from '@/components/ColorPicker';
import Navbar from '@/components/Navbar';
import TagsNavigationButtons from '@/components/TagsNavigationButtons';

export default {
  components: {
    ColorPicker,
    Navbar,
    TagsNavigationButtons,
  },
  mixins: [tagsMixin],
  data() {
    return {
      links: ['dashboard', {label: 'tags', active: false}, 'tagsManagement'],
      loading: false,
      busy: false,
      newTagGroupId_: null,
      newTagName: '',
      newTagColor: this.generateRandomColor(),
      showCreateTagDialog: false,
      showCreateTagColorPickerMenu: false,
      tagBeingDeleted: null,
      tagBeingEdited: null,
      tagNameMaxLength: 60,
    };
  },
  computed: {
    searchText: {
      get() {
        return this.$store.state.common.tagsSearchText;
      },
      set(text) {
        return this.$store.commit('SET_TAGS_SEARCH_TEXT', text);
      },
    },
    searchTextHint() {
      if (!this.tags.length || !this.searchText) {
        return '';
      }
      return `${this.filteredTags.length}/${this.tags.length}`;
    },
    searchTextPlaceholder() {
      if (!this.tags.length) {
        return this.$t('search') + ' ...';
      }
      return this.$t('search') + ` (${this.tags.length}) ...`;
    },
    rules() {
      return {
        tagNameMinLength: (v) => (!!v && v.length > 0) || this.$t('tooShort'),
        tagNameMaxLength: (v) =>
          !v || v.length <= this.tagNameMaxLength || this.$t('tooLong'),
        newTagUniqueName: (v) =>
          !this.isTagNameAlreadyUsed(v) || this.$t('nameAlreadyInUse'),
        editedTagUniqueName: (v) =>
          !this.isTagNameAlreadyUsed(v, this.tagBeingEdited) ||
          this.$t('nameAlreadyInUse'),
      };
    },
    uploadgroups() {
      return this.$store.getters.uploadgroups;
    },
    uploadgroupIds() {
      return this.uploadgroups.map((group) => group.id);
    },
    orderedUploadgroups() {
      return orderBy(this.uploadgroups, (group) => group.name.toLowerCase());
    },
    tags() {
      return this.$store.state.common.tags;
    },
    filteredTags() {
      if (!this.searchText) {
        return this.tags;
      }

      return this.tags.filter((tag) => {
        return (
          matchesFilter(tag.name, this.searchText) ||
          matchesFilter(tag.group.name, this.searchText)
        );
      });
    },
    orderedTags() {
      return orderBy(this.filteredTags, (tag) => tag.name.toLowerCase());
    },
    newTagNameIsValid() {
      return (
        this.isTagNameValid(this.newTagName) &&
        !this.isTagNameAlreadyUsed(this.newTagName)
      );
    },
    canSubmitCreateTagForm() {
      return this.newTagNameIsValid && !!this.newTagGroupId;
    },
    editedTagNameIsValid() {
      return (
        !!this.tagBeingEdited &&
        this.isTagNameValid(this.tagBeingEdited.name) &&
        !this.isTagNameAlreadyUsed(
          this.tagBeingEdited.name,
          this.tagBeingEdited
        )
      );
    },
    hasMoreThanOneUploadGroup() {
      return this.uploadgroups.length > 1;
    },
    newTagGroupId() {
      let groupId = this.uploadgroups.map((group) => group.id)[0];
      if (this.hasMoreThanOneUploadGroup) {
        groupId = this.newTagGroupId_;
      }
      return groupId;
    },
  },
  watch: {
    showCreateTagDialog: function (show) {
      if (show) {
        this.newTagColor = this.generateRandomColor();
        this.$nextTick(() => {
          this.$refs.newTagNameInput.focus();
        });
      }
    },
  },
  mounted() {
    this.fetchTags();
  },
  methods: {
    isTagUsed(tag) {
      return (
        tag.num_contents.radio +
          tag.num_contents.tv +
          tag.num_decoders.radio +
          tag.num_decoders.tv >
        0
      );
    },
    generateRandomColor() {
      return tinycolor.random().toHexString();
    },
    confirmCreateNewTagDialog() {
      this.addTag();
      this.showCreateTagDialog = false;
    },
    canManageTag(tag) {
      const groupId = tag.group;
      return groupId && this.uploadgroupIds.includes(groupId);
    },
    getEditModeColorButtonStyle(tag) {
      let background = tag.color || this.getTagColor(tag);
      if (this.isBeingEdited(tag)) {
        background =
          this.tagBeingEdited.color || this.getTagColor(this.tagBeingEdited);
      }
      return {background};
    },
    goToContentsListFilteredByTag(tag) {
      this.$store.commit('INVALIDATE_CONTENTS_CACHE');
      this.$store.commit('SET_CONTENTS_QUERY_SELECTED_TAG_IDS', [tag.id]);
      this.$store.commit('SET_FORCE_REFETCH_CONTENTS_ON_NEXT_VISIT', true);
      this.$router.push({name: 'contents'});
    },
    goToPlayersListFilteredByTag(tag) {
      this.$store.commit('SET_TAGGABLE_DECODERS_SEARCH_TEXT', '');
      this.$store.commit('SET_TAGGABLE_DECODERS_SELECTED_TAG_IDS', [tag.id]);
      this.$router.push({name: 'taggabledecoders'});
    },
    onTagColorChanged(color) {
      this.tagBeingDeleted.color = color;
    },
    getGroupNameForId(groupId) {
      const group = this.uploadgroups.filter((group) => group.id == groupId)[0];
      if (group) {
        return group.name;
      }
      return null;
    },
    isTagNameAlreadyUsed(tagName, ignoreThisTag = null) {
      const names = this.orderedTags
        .filter((tag) => {
          return !ignoreThisTag || tag.id !== ignoreThisTag.id;
        })
        .map((tag) => tag.name.toLowerCase());
      return names.indexOf(tagName.toLowerCase()) !== -1;
    },
    isTagNameValid(tagName) {
      return (
        !!tagName &&
        tagName.length > 0 &&
        tagName.length <= this.tagNameMaxLength
      );
    },
    isBeingNormal(tag) {
      return !this.isBeingEdited(tag) && !this.isBeingDeleted(tag);
    },
    isBeingEdited(tag) {
      return this.tagBeingEdited && this.tagBeingEdited.id == tag.id;
    },
    isBeingDeleted(tag) {
      return this.tagBeingDeleted == tag;
    },
    startDeleting(tag) {
      this.cancelEditing();

      this.tagBeingDeleted = tag;
    },
    cancelDeleting() {
      this.tagBeingDeleted = null;
    },
    startEditing(tag) {
      this.cancelDeleting();

      this.tagBeingEdited = deepCopyObject(tag);

      this.$nextTick(() => {
        this.$refs.tagNameInput[0].focus();
      });
    },
    cancelEditing() {
      this.tagBeingEdited = null;
    },
    addTag() {
      if (!this.newTagName) {
        return;
      }

      const tag = {
        color: this.newTagColor,
        group: this.newTagGroupId,
        name: this.newTagName,
      };

      this.busy = true;
      this.$api
        .createTag(tag)
        .then(() => {
          const message = this.$t('tagCreatedSuccess') + ': ' + tag.name;
          this.$bus.$emit('alert-success', {
            message: message,
            translate: false,
            timeout: 1500,
          });
          this.newTagName = '';

          this.fetchTags();
        })
        .catch((error) => {
          console.error(error);
          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .finally(() => {
          this.busy = false;
        });
    },
    saveEditedTag() {
      if (!this.editedTagNameIsValid) {
        return;
      }

      this.busy = true;
      this.$api
        .updateTag(deepCopyObject(this.tagBeingEdited))
        .then(() => {
          this.fetchTags();
          this.cancelEditing();
        })
        .catch((error) => {
          console.error(error);
          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .finally(() => {
          this.busy = false;
        });
    },
    removeTag(tag) {
      this.busy = true;
      this.$api
        .deleteTag(tag.id)
        .then(() => {
          const message = this.$t('tagRemovedSuccess') + ': ' + tag.name;
          this.$bus.$emit('alert-success', {
            message: message,
            translate: false,
            timeout: 1500,
          });

          this.fetchTags();
          this.cancelDeleting();
        })
        .catch((error) => {
          console.error(error);
          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .finally(() => {
          this.busy = false;
        });
    },
    fetchTags({spinner = true} = {}) {
      if (spinner) this.loading = true;
      return this.$store.dispatch('LIST_TAGS').finally(() => {
        if (spinner) this.loading = false;
      });
    },
  },
};
</script>

<style scoped>
.highlight-color-on-hover:hover * {
  color: var(--msr-color-primary) !important;
}

.tag-card:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.tags-list-transition-enter-active,
.tags-list-transition-leave-active {
  transition: all 0.25s ease-in-out;
}
.tags-list-transition-enter,
.tags-list-transition-leave-to {
  opacity: 0;
  transform: translateX(50px);
}
</style>



// WEBPACK FOOTER //
// src/pages/Tags.vue